import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldAlert, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = {
  LOCKED: { label: 'Blocage', cls: 'bg-amber-100 text-amber-800' },
  ALERT: { label: 'Alerte', cls: 'bg-red-100 text-red-800' },
  UNLOCKED: { label: 'Déblocage', cls: 'bg-emerald-100 text-emerald-800' },
};

const Stat = ({ label, value, id }) => (
  <div className="border rounded-md px-3 py-2" data-testid={`security-stat-${id}`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-xl font-semibold">{value}</div>
  </div>
);

const EventRow = ({ e }) => (
  <div className="flex flex-wrap items-center gap-2 border-b py-2 text-sm" data-testid={`security-event-${e.id}`}>
    <span className="text-gray-500 w-32">{new Date(e.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
    <Badge className={KINDS[e.kind]?.cls}>{KINDS[e.kind]?.label || e.kind}</Badge>
    <span><strong>{e.user_name || e.email}</strong> · {e.email}</span>
    <span className="text-gray-600">— {e.details}{e.actor_name ? ` (par ${e.actor_name})` : ''}</span>
  </div>
);

const SuspiciousAccounts = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div className="border border-red-300 bg-red-50 rounded-md p-3 space-y-2" data-testid="security-suspicious-accounts">
      <div className="flex items-center gap-2 font-semibold text-red-800"><AlertTriangle className="w-4 h-4" /> Comptes suspects ce mois-ci (3 blocages ou plus)</div>
      {items.map((s) => (
        <div key={`${s.tenant_id}-${s.email}`} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`suspicious-account-${s.user_id}`}>
          <Badge className="bg-red-600 text-white">{s.count} blocages</Badge>
          <span><strong>{s.user_name || s.email}</strong> · {s.email}</span>
          <span className="text-gray-600">— dernier le {new Date(s.last_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
        </div>
      ))}
      <p className="text-xs text-red-700">Vérifiez avec l’utilisateur s’il a oublié son mot de passe ; sinon, il peut s’agir d’une tentative d’intrusion.</p>
    </div>
  );
};

export const SecurityHistoryCard = () => {
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/security-history`, { params: { days } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);
  if (!data) return null;
  const s = data.summary;
  return (
    <Card data-testid="security-history-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Historique de sécurité</CardTitle>
          <CardDescription>Blocages de connexion, alertes envoyées et déblocages.</CardDescription>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="security-history-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 derniers jours</SelectItem>
            <SelectItem value="30">30 derniers jours</SelectItem>
            <SelectItem value="90">90 derniers jours</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-4">
        <SuspiciousAccounts items={data.suspicious} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat id="locked" label="Blocages" value={s.LOCKED} />
          <Stat id="accounts" label="Comptes concernés" value={s.accounts} />
          <Stat id="alerts" label="Alertes" value={s.ALERT} />
          <Stat id="unlocked" label="Déblocages manuels" value={s.UNLOCKED} />
        </div>
        {data.events.length === 0
          ? <p className="text-sm text-gray-500" data-testid="security-history-empty">Aucun événement de sécurité sur la période.</p>
          : <div className="max-h-80 overflow-y-auto">{data.events.map((e) => <EventRow key={e.id} e={e} />)}</div>}
      </CardContent>
    </Card>
  );
};

export default SecurityHistoryCard;
